import { atom, AtomEffect } from "recoil";

import { FilterRule } from "../types/filter";

export const TIME_FILTER_STORAGE_KEY = "timeFilterVal";

// The highest-precision range the user actually picked, kept separate from TIME_FILTER_STORAGE_KEY
// (which always reflects the *currently applied*, possibly-clamped-for-this-table value). Tables
// with a coarser dateFilterMode (e.g. "date"-only) clamp from this raw value without overwriting
// it, so switching back to a finer-grained table (e.g. "minute") restores the original precision
// instead of the coarse 00:00:00/23:59:59 the in-between table applied.
export const TIME_FILTER_RAW_STORAGE_KEY = "timeFilterRawVal";

// Helper to get the raw (highest-precision picked) value from sessionStorage
export const getPersistedRawTimeFilterValue = (): string | null => {
    return sessionStorage.getItem(TIME_FILTER_RAW_STORAGE_KEY);
};

// Helper to set the raw (highest-precision picked) value to sessionStorage
export const setPersistedRawTimeFilterValue = (value: string): void => {
    sessionStorage.setItem(TIME_FILTER_RAW_STORAGE_KEY, value);
};

// Helper to remove the raw (highest-precision picked) value from sessionStorage
export const removePersistedRawTimeFilterValue = (): void => {
    sessionStorage.removeItem(TIME_FILTER_RAW_STORAGE_KEY);
};

// Custom sessionStorage effect that only persists the date range value
// This allows the dataIndex to change per table while keeping the date range
const timeFilterSessionStorageEffect: AtomEffect<FilterRule | null> = ({ onSet }) => {
    // On change, save only the value to sessionStorage
    onSet((newValue, _, isReset) => {
        if (isReset || !newValue) {
            removePersistedTimeFilterValue();
            removePersistedRawTimeFilterValue();
        } else {
            // Safely validate and store the value
            if (typeof newValue.value === "string") {
                setPersistedTimeFilterValue(newValue.value);
            } else {
                console.error("TimeFilter: Expected string value for time filter, got:", typeof newValue.value);
            }
        }
    });
};

export const timeFilterAtom = atom<FilterRule | null>({
    key: "timeFilterAtom",
    default: null,
    effects: [timeFilterSessionStorageEffect],
});

// Helper to get the persisted value from sessionStorage
export const getPersistedTimeFilterValue = (): string | null => {
    return sessionStorage.getItem(TIME_FILTER_STORAGE_KEY);
};

// Helper to set persisted value to sessionStorage
export const setPersistedTimeFilterValue = (value: string): void => {
    sessionStorage.setItem(TIME_FILTER_STORAGE_KEY, value);
};

// Helper to remove the persisted value from sessionStorage
export const removePersistedTimeFilterValue = (): void => {
    sessionStorage.removeItem(TIME_FILTER_STORAGE_KEY);
};
